import React, { useState } from 'react';

export default function CatalogRenderer() {
  const [catalogData, setCatalogData] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [jsonInput, setJsonInput] = useState('');

  // Sample data
  const loadSampleData = () => {
    const sampleCatalog = {
      catalogName: 'Spring 2024 Course Catalog',
      catalogYear: 2024,
      catalogDate: '2024-01-15',
      isActive: true,
      sections: [
        {
          sectionName: 'Mathematics Department',
          subSections: [
            {
              subSectionName: 'Core Mathematics Courses',
              gridSystem: '2-grid',
              alignment: 'left',
              allowDynamicData: true,
              contents: [
                { contentType: 'Heading', contentValue: 'Advanced Calculus' },
                {
                  contentType: 'Text',
                  contentValue:
                    'Explore differential and integral calculus with real-world applications. Prerequisites: Calculus I & II',
                },
                { contentType: 'Heading', contentValue: 'Linear Algebra' },
                {
                  contentType: 'Text',
                  contentValue:
                    'Vector spaces, matrices, and linear transformations. Essential for computer science and engineering.',
                },
              ],
            },
            {
              subSectionName: 'Course Schedule',
              gridSystem: 'no-grid',
              alignment: 'center',
              allowDynamicData: false,
              contents: [
                { contentType: 'Border', contentValue: '' },
                {
                  contentType: 'Table',
                  contentValue:
                    'Course Code|Course Name|Credits|Instructor\nMATH301|Advanced Calculus|4|Dr. Smith\nMATH302|Linear Algebra|3|Prof. Johnson\nMATH401|Abstract Algebra|4|Dr. Williams',
                },
              ],
            },
          ],
        },
        {
          sectionName: 'Computer Science',
          subSections: [
            {
              subSectionName: 'Programming Courses',
              gridSystem: '3-grid',
              alignment: 'center',
              allowDynamicData: true,
              contents: [
                { contentType: 'Heading', contentValue: 'Data Structures' },
                {
                  contentType: 'Text',
                  contentValue:
                    'Master arrays, linked lists, trees, and graphs',
                },
                { contentType: 'Heading', contentValue: 'Algorithms' },
                {
                  contentType: 'Text',
                  contentValue:
                    'Learn sorting, searching, and optimization techniques',
                },
                { contentType: 'Heading', contentValue: 'Web Development' },
                {
                  contentType: 'Text',
                  contentValue:
                    'Build modern web applications with React and Node.js',
                },
              ],
            },
          ],
        },
      ],
    };

    setCatalogData(sampleCatalog);
    setJsonInput(JSON.stringify(sampleCatalog, null, 2));
  };

  const handleRender = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setCatalogData(parsed);
      setShowEditor(false);
    } catch (e) {
      alert('Invalid JSON data. Please check your input.');
    }
  };

  React.useEffect(() => {
    loadSampleData();
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
        }}
      >
        {catalogData ? (
          <>
            <CatalogHeader catalog={catalogData} />
            {catalogData.sections && catalogData.sections.length > 0 ? (
              catalogData.sections.map((section, idx) => (
                <Section key={idx} section={section} />
              ))
            ) : (
              <EmptyState message='No sections available' />
            )}
          </>
        ) : (
          <EmptyState
            message='No Catalog Data'
            subtitle='Load sample data or paste your JSON'
          />
        )}
      </div>

      <Controls
        onToggleEditor={() => setShowEditor(!showEditor)}
        onRender={handleRender}
        onLoadSample={loadSampleData}
      />

      {showEditor && (
        <Editor
          value={jsonInput}
          onChange={setJsonInput}
          onApply={handleRender}
          onCancel={() => setShowEditor(false)}
        />
      )}
    </div>
  );
}

function CatalogHeader({ catalog }) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        color: 'white',
        padding: '40px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          right: '-50%',
          width: '200%',
          height: '200%',
          background:
            'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
          animation: 'pulse 15s ease-in-out infinite',
        }}
      />

      <h1
        style={{
          fontSize: '48px',
          fontWeight: '700',
          marginBottom: '10px',
          position: 'relative',
          zIndex: 1,
          textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
        }}
      >
        {catalog.catalogName || 'School Catalog'}
      </h1>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '30px',
          fontSize: '16px',
          opacity: 0.95,
          position: 'relative',
          zIndex: 1,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '8px 20px',
            borderRadius: '20px',
          }}
        >
          📅 {catalog.catalogDate || 'N/A'}
        </span>
        <span
          style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '8px 20px',
            borderRadius: '20px',
          }}
        >
          📚 Year {catalog.catalogYear || 'N/A'}
        </span>
        <span
          style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '8px 20px',
            borderRadius: '20px',
          }}
        >
          {catalog.isActive ? '✅ Active' : '⏸️ Inactive'}
        </span>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}

function Section({ section }) {
  return (
    <div
      style={{
        padding: '50px 40px',
        borderBottom: '2px solid #f0f0f0',
      }}
    >
      <h2
        style={{
          fontSize: '36px',
          fontWeight: '700',
          color: '#1e3c72',
          marginBottom: '30px',
          paddingBottom: '15px',
          borderBottom: '4px solid #667eea',
          display: 'inline-block',
        }}
      >
        {section.sectionName || 'Untitled Section'}
      </h2>

      {section.subSections &&
        section.subSections.length > 0 &&
        section.subSections.map((sub, idx) => (
          <SubSection key={idx} subSection={sub} />
        ))}
    </div>
  );
}

function SubSection({ subSection }) {
  const getGridStyle = () => {
    const baseStyle = { marginBottom: '15px' };

    if (subSection.gridSystem === '2-grid') {
      return {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
      };
    } else if (subSection.gridSystem === '3-grid') {
      return {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
      };
    }
    return { display: 'block' };
  };

  const alignmentStyle = {
    textAlign: subSection.alignment || 'left',
  };

  return (
    <div
      style={{
        marginBottom: '40px',
        padding: '30px',
        background: '#f8f9fa',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 8px 15px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
      }}
    >
      <h3
        style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#2a5298',
          marginBottom: '20px',
        }}
      >
        {subSection.subSectionName || 'Untitled SubSection'}
        {subSection.allowDynamicData && (
          <span
            style={{
              display: 'inline-block',
              background: '#4caf50',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              marginLeft: '10px',
              verticalAlign: 'middle',
            }}
          >
            DYNAMIC
          </span>
        )}
      </h3>

      <div style={{ ...getGridStyle(), ...alignmentStyle }}>
        {subSection.contents &&
          subSection.contents.length > 0 &&
          subSection.contents.map((content, idx) => (
            <ContentItem key={idx} content={content} />
          ))}
      </div>
    </div>
  );
}

function ContentItem({ content }) {
  const renderContent = () => {
    switch (content.contentType) {
      case 'Heading':
        return (
          <h4
            style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#1e3c72',
              margin: '20px 0 15px 0',
              lineHeight: '1.3',
            }}
          >
            {content.contentValue}
          </h4>
        );

      case 'Text':
        return (
          <p
            style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#333',
              margin: '10px 0',
            }}
          >
            {content.contentValue}
          </p>
        );

      case 'Image':
        return (
          <img
            src={content.contentValue}
            alt='Content'
            style={{
              width: '100%',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              margin: '15px 0',
              transition: 'transform 0.3s ease',
            }}
            onMouseEnter={(e) => (e.target.style.transform = 'scale(1.02)')}
            onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
            onError={(e) => {
              e.target.src =
                "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23ddd' width='400' height='300'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EImage Not Found%3C/text%3E%3C/svg%3E";
            }}
          />
        );

      case 'Table':
        return <TableContent value={content.contentValue} />;

      case 'Border':
        return (
          <div
            style={{
              height: '3px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              margin: '30px 0',
              borderRadius: '2px',
            }}
          />
        );

      default:
        return (
          <p
            style={{
              fontSize: '16px',
              lineHeight: '1.8',
              color: '#333',
              margin: '10px 0',
            }}
          >
            {content.contentValue}
          </p>
        );
    }
  };

  return <div style={{ marginBottom: '15px' }}>{renderContent()}</div>;
}

function TableContent({ value }) {
  if (!value) return null;

  const rows = value.split('\n').filter((row) => row.trim());
  if (rows.length === 0) return null;

  const headers = rows[0].split('|').map((h) => h.trim());
  const dataRows = rows.slice(1);

  return (
    <div style={{ margin: '20px 0', overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          background: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <thead>
          <tr>
            {headers.map((header, idx) => (
              <th
                key={idx}
                style={{
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '15px',
                  fontWeight: '600',
                  textAlign: 'left',
                }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, rowIdx) => {
            const cells = row.split('|').map((c) => c.trim());
            return (
              <tr
                key={rowIdx}
                style={{ transition: 'background 0.2s' }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = '#f5f5f5')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'white')
                }
              >
                {cells.map((cell, cellIdx) => (
                  <td
                    key={cellIdx}
                    style={{
                      padding: '12px 15px',
                      borderBottom:
                        rowIdx === dataRows.length - 1
                          ? 'none'
                          : '1px solid #e0e0e0',
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Controls({ onToggleEditor, onRender, onLoadSample }) {
  const buttonStyle = {
    padding: '12px 30px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  };

  return (
    <div
      style={{
        padding: '30px 40px',
        background: '#f8f9fa',
        display: 'flex',
        gap: '15px',
        flexWrap: 'wrap',
        maxWidth: '1200px',
        margin: '20px auto 0',
        borderRadius: '16px',
      }}
    >
      <button
        style={{
          ...buttonStyle,
          background: 'white',
          color: '#667eea',
          border: '2px solid #667eea',
        }}
        onClick={onToggleEditor}
        onMouseEnter={(e) => {
          e.target.style.background = '#667eea';
          e.target.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'white';
          e.target.style.color = '#667eea';
        }}
      >
        📝 Edit Data
      </button>
      <button
        style={{
          ...buttonStyle,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
        onClick={onRender}
        onMouseEnter={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 6px 12px rgba(102, 126, 234, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
        }}
      >
        🎨 Render Catalog
      </button>
      <button
        style={{
          ...buttonStyle,
          background: 'white',
          color: '#667eea',
          border: '2px solid #667eea',
        }}
        onClick={onLoadSample}
        onMouseEnter={(e) => {
          e.target.style.background = '#667eea';
          e.target.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'white';
          e.target.style.color = '#667eea';
        }}
      >
        📚 Load Sample
      </button>
      <button
        style={{
          ...buttonStyle,
          background: 'white',
          color: '#667eea',
          border: '2px solid #667eea',
        }}
        onClick={() => window.print()}
        onMouseEnter={(e) => {
          e.target.style.background = '#667eea';
          e.target.style.color = 'white';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'white';
          e.target.style.color = '#667eea';
        }}
      >
        🖨️ Print
      </button>
    </div>
  );
}

function Editor({ value, onChange, onApply, onCancel }) {
  return (
    <div
      style={{
        padding: '30px 40px',
        background: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        maxWidth: '1200px',
        margin: '20px auto 0',
        borderRadius: '16px',
      }}
    >
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder='Paste your catalog JSON data here...'
        style={{
          width: '100%',
          minHeight: '300px',
          padding: '15px',
          border: '2px solid #e0e0e0',
          borderRadius: '8px',
          fontFamily: 'monospace',
          fontSize: '14px',
          resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', gap: '15px' }}>
        <button
          onClick={onApply}
          style={{
            padding: '12px 30px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          ✅ Apply & Render
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '12px 30px',
            border: '2px solid #667eea',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            background: 'white',
            color: '#667eea',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          }}
        >
          ❌ Cancel
        </button>
      </div>
    </div>
  );
}

function EmptyState({ message, subtitle }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 40px', color: '#999' }}>
      <svg
        viewBox='0 0 24 24'
        fill='currentColor'
        style={{
          width: '120px',
          height: '120px',
          marginBottom: '20px',
          opacity: 0.3,
        }}
      >
        <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z' />
      </svg>
      <h2>{message}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}
